import { api, encodeObjectPath, downloadFile, request } from './client';
import type {
  ProjectWorkload,
  ActivityReport,
  AdminHardwareTier,
  AdminInventory,
  AdminOverview,
  Agent,
  AgentGovernanceReport,
  AgentInput,
  AgentMandate,
  AgentRun,
  Workflow,
  WorkflowInput,
  WorkflowRun,
  AgentTeam,
  AIServicesStatus,
  ApiToken,
  App,
  AppRevision,
  AppUsage,
  AssignableRole,
  AuditEvent,
  BackupConfigStatus,
  BackupRun,
  Build,
  Extract,
  ExtractAccess,
  CreatedUser,
  CronJob,
  Dataset,
  DatasetAccess,
  DatasourceAccess,
  DatasetUsage,
  Datasource,
  DatasourceDefinition,
  DataUsageReport,
  DockerfileResponse,
  EffectiveSetting,
  EgressProfile,
  EgressRule,
  Environment,
  Execution,
  HardwareTier,
  HealthHistory,
  HealthReport,
  Job,
  LogsResponse,
  ModuleInfo,
  Ontology,
  OntologyAccess,
  OntologyCompleteness,
  OntologyScan,
  OntologyFreshness,
  OntologyManifestSummary,
  OntologyQueryItem,
  Organization,
  OrganizationAdmin,
  OrganizationPolicy,
  PlatformRole,
  PlatformRoleAssignment,
  MyCapabilities,
  OrganizationMember,
  OwnedResources,
  PlatformOverview,
  PlatformUser,
  PodInfo,
  Project,
  ProjectMember,
  ProjectOrganizationRole,
  ProjectQuota,
  ProjectQuotaState,
  ProjectTeamRole,
  ProjectToken,
  ProjectVariable,
  RbacMatrixReport,
  RbacPolicyResponse,
  RbacPolicyRow,
  Repository,
  SearchResult,
  Secret,
  SmtpSettings,
  SmtpState,
  SoftwareInventory,
  StorageCapacityReport,
  StorageEndpoint,
  StorageObject,
  Team,
  TeamMember,
  TeamUsageReport,
  UsageSample,
  UsageTotal,
  UserPreferences,
  VersionInfo,
  Workspace,
  ServiceAccount,
  MountedProjectList,
  OntologyCard,
  DatasetPathLayout,
  DatasetPathLayoutTrial,
  WorkspaceStartup,
  ExtractIntegrity,
  ExtractMembers,
  DeletionCost,
} from './types';

const V1 = '/api/v1';

export const platformApi = {
  version: () => api.get<VersionInfo>(`${V1}/version`),
  overview: () => api.get<PlatformOverview>(`${V1}/platform/overview`),
  aiServices: () => api.get<AIServicesStatus>(`${V1}/ai-services/status`),
  // Ce qu'on peut donner a quelqu'un ici : les trois roles de la plateforme,
  // plus ceux que l'installation a ecrits la ou cette edition sait les faire
  // respecter. Un role qui ne serait pas honore n'est pas propose.
  assignableRoles: () => api.get<{ roles: AssignableRole[] }>(`${V1}/roles`),
  agents: () => api.list<Agent>(`${V1}/agents`),
  createAgent: (body: AgentInput) => api.post<Agent>(`${V1}/agents`, body),
  updateAgent: (id: string, body: Partial<AgentInput>) => api.patch<Agent>(`${V1}/agents/${id}`, body),
  deleteAgent: (id: string) => api.delete<void>(`${V1}/agents/${id}`),
  agentRuns: (id: string) => api.list<AgentRun>(`${V1}/agents/${id}/runs`),
  runAgent: (id: string) => api.post<AgentRun>(`${V1}/agents/${id}/run`, {}),
  askAgent: (id: string, message: string) =>
    api.post<AgentRun>(`${V1}/agents/${id}/ask`, { message }),
  // Workflows (ADR-046) : ce qui s'execute quand un agent travaille.
  workflows: () => api.list<Workflow>(`${V1}/workflows`),
  createWorkflow: (body: WorkflowInput) => api.post<Workflow>(`${V1}/workflows`, body),
  updateWorkflow: (id: string, body: Partial<WorkflowInput> & { enabled?: boolean }) =>
    api.patch<Workflow>(`${V1}/workflows/${id}`, body),
  deleteWorkflow: (id: string) => api.delete<void>(`${V1}/workflows/${id}`),
  workflowRuns: (id: string) => api.list<WorkflowRun>(`${V1}/workflows/${id}/runs`),
  runWorkflow: (id: string) => api.post<WorkflowRun>(`${V1}/workflows/${id}/runs`, {}),
  approveWorkflowRun: (runId: string) =>
    api.post<WorkflowRun>(`${V1}/workflow-runs/${runId}/approve`, {}),
  rejectWorkflowRun: (runId: string) =>
    api.post<WorkflowRun>(`${V1}/workflow-runs/${runId}/reject`, {}),

  agentTeams: () => api.list<AgentTeam>(`${V1}/agent-teams`),
  createAgentTeam: (body: { name: string; purpose: string; projectId?: string }) =>
    api.post<AgentTeam>(`${V1}/agent-teams`, body),
  updateAgentTeam: (id: string, body: { name?: string; purpose?: string }) =>
    api.patch<AgentTeam>(`${V1}/agent-teams/${id}`, body),
  deleteAgentTeam: (id: string) => api.delete<void>(`${V1}/agent-teams/${id}`),
  agentMandates: (teamId: string) => api.list<AgentMandate>(`${V1}/agent-teams/${teamId}/mandates`),
  createAgentMandate: (teamId: string, body: { leadId: string; memberId: string; action: string }) =>
    api.post<AgentMandate>(`${V1}/agent-teams/${teamId}/mandates`, body),
  deleteAgentMandate: (teamId: string, mandateId: string) =>
    api.delete<void>(`${V1}/agent-teams/${teamId}/mandates/${mandateId}`),
  hardwareTiers: () => api.list<HardwareTier>(`${V1}/hardware-tiers`),
  preferences: () => api.get<UserPreferences>(`${V1}/user/preferences`),
  apiTokens: () => api.list<ApiToken>(`${V1}/user/api-tokens`),
  createApiToken: (input: { name: string; expiresInDays?: number; scopes?: string[] }) =>
    api.post<{ token: ApiToken; secret: string; note: string }>(`${V1}/user/api-tokens`, input),
  revokeApiToken: (tokenId: string) =>
    api.delete<void>(`${V1}/user/api-tokens/${encodeURIComponent(tokenId)}`),
  organizations: () => api.list<Organization>(`${V1}/organizations`),
  /** The caller's own memberships, read from the identity provider rather than
   *  from the token: a realm that does not put the claim in the token, or a
   *  membership granted since sign-in, would otherwise show as none. */
  myOrganizations: () => api.list<Organization>(`${V1}/user/organizations`),
  /** Ou cette personne administre. Le jeton dit qui elle est, pas ce qu'une
   *  organisation a decide a son sujet, donc la porte se demande au serveur. */
  /** Ce que ce role peut creer. L'interface cache ce qui est refuse en
   *  demandant ici, jamais en reimplementant la regle. */
  myCapabilities: () => api.get<MyCapabilities>(`${V1}/user/capabilities`),
  administeredOrganizations: () =>
    api.list<{ id: string; name: string; alias?: string }>(`${V1}/user/administered-organizations`),
};

export const searchApi = {
  /** Results are scoped server-side by the same rules the listing endpoints
   *  use, so this can never surface anything the caller could not open. */
  search: (query: string) => api.list<SearchResult>(`${V1}/search`, { params: { q: query } }),
};

export const projectsApi = {
  list: () => api.list<Project>(`${V1}/projects`),
  create: (input: { name: string; description?: string }) => api.post<Project>(`${V1}/projects`, input),
  update: (
    projectId: string,
    input: { name?: string; description?: string; workspaceStorageSize?: string },
  ) =>
    api.put<Project>(`${V1}/projects/${projectId}`, input),
  /** Ce que la suppression detruirait, lu avant de demander quoi que ce soit. */
  workloads: (projectId: string) =>
    api.get<{ items: ProjectWorkload[]; running: number }>(`${V1}/projects/${projectId}/workloads`),
  remove: (projectId: string, force = false) =>
    api.delete<void>(`${V1}/projects/${projectId}${force ? '?force=true' : ''}`),
  setOwner: (projectId: string, input: { ownerType: string; ownerId: string }) =>
    api.put<Project>(`${V1}/projects/${projectId}/ownership`, input),
  usage: (projectId: string) =>
    api.get<{ total: UsageTotal; samples: UsageSample[] }>(
      `${V1}/projects/${encodeURIComponent(projectId)}/usage`,
    ),
  quota: (projectId: string) =>
    api.get<ProjectQuotaState>(`${V1}/projects/${encodeURIComponent(projectId)}/quota`),
  setMemberRole: (projectId: string, userId: string, role: string) =>
    api.put<void>(`${V1}/projects/${projectId}/members/${userId}/role`, { role }),
  invite: (projectId: string, input: { userId: string; role: string }) =>
    api.post<ProjectMember>(`${V1}/projects/${projectId}/invitations`, input),

  datasets: (projectId: string) => api.list<Dataset>(`${V1}/projects/${projectId}/datasets`),
  attachDataset: (projectId: string, datasetId: string) =>
    api.put<void>(`${V1}/projects/${projectId}/datasets/${datasetId}`),
  detachDataset: (projectId: string, datasetId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/datasets/${datasetId}`),

  datasources: (projectId: string) => api.list<Datasource>(`${V1}/projects/${projectId}/datasources`),
  attachDatasource: (projectId: string, datasourceId: string) =>
    api.put<void>(`${V1}/projects/${projectId}/datasources/${datasourceId}`),
  detachDatasource: (projectId: string, datasourceId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/datasources/${datasourceId}`),

  ontologies: (projectId: string) => api.list<Ontology>(`${V1}/projects/${projectId}/ontologies`),
  attachOntology: (projectId: string, ontologyId: string) =>
    api.put<void>(`${V1}/projects/${projectId}/ontologies/${ontologyId}`),
  /** The extracts this project mounts. An extract is a frozen selection over a
   *  dataset; which projects use it is a separate question, and more than one
   *  may answer yes. */
  extracts: (projectId: string) => api.list<Extract>(`${V1}/projects/${projectId}/extracts`),
  attachExtract: (projectId: string, extractId: string) =>
    api.put<void>(`${V1}/projects/${projectId}/extracts/${extractId}`, {}),
  detachExtract: (projectId: string, extractId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/extracts/${extractId}`),
  detachOntology: (projectId: string, ontologyId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/ontologies/${ontologyId}`),
  ontology: (projectId: string) => api.get<Ontology>(`${V1}/projects/${projectId}/ontology`),

  repositories: (projectId: string) => api.list<Repository>(`${V1}/projects/${projectId}/repositories`),
  attachRepository: (projectId: string, repositoryId: string) =>
    api.put<void>(`${V1}/projects/${projectId}/repositories/${repositoryId}`),
  detachRepository: (projectId: string, repositoryId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/repositories/${repositoryId}`),

  files: (projectId: string, path?: string) =>
    api.list<StorageObject>(
      path
        ? `${V1}/projects/${projectId}/files/${encodeObjectPath(path)}`
        : `${V1}/projects/${projectId}/files`,
    ),
  createFolder: (projectId: string, path: string) =>
    api.post<void>(`${V1}/projects/${projectId}/folders`, { path }),
  deleteFile: (projectId: string, path: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/files/${encodeObjectPath(path)}`),

  egressRules: (projectId: string) => api.list<EgressRule>(`${V1}/projects/${projectId}/egress/rules`),
};

export interface CreateWorkspaceInput {
  projectId: string;
  name?: string;
  /** jupyter | vscode | rstudio. Selects the default image when `image` is
   *  omitted; the API rejects any other value. */
  ide?: string;
  /** Full image reference, taken from the chosen environment. */
  image?: string;
  hardwareTier?: string;
  storageSize?: string;
  /** "dataset" (default) mounts the attached datasets whole; "extracts" mounts
   *  only the declared selections, filled into the cache by a container beside
   *  the workspace, with the buckets mounted nowhere the person can reach. */
}

export const projectVariablesApi = {
  /** The answer carries `canReadValues`, which the screen needs before it
   *  renders: a viewer sees names, an editor sees values. */
  list: async (projectId: string) =>
    api.get<{ items: ProjectVariable[]; canReadValues: boolean }>(
      `${V1}/projects/${projectId}/variables`,
    ),
  set: (projectId: string, name: string, value: string, description?: string) =>
    api.put<ProjectVariable>(`${V1}/projects/${projectId}/variables/${encodeURIComponent(name)}`, {
      value,
      description: description ?? '',
    }),
  remove: (projectId: string, name: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/variables/${encodeURIComponent(name)}`),
};

export const workspacesApi = {
  startup: (workspaceId: string) =>
    api.get<WorkspaceStartup>(`${V1}/workspaces/${workspaceId}/startup`),
  list: (projectId?: string) =>
    api.list<Workspace>(`${V1}/workspaces`, projectId ? { params: { projectId } } : undefined),
  create: (input: CreateWorkspaceInput) => api.post<Workspace>(`${V1}/workspaces`, input),
  remove: (workspaceId: string) => api.delete<void>(`${V1}/workspaces/${workspaceId}`),
  /** Replaces the workspace's pod under the same name, from its own live
   *  specification. The address stays valid, so there is nothing to reopen -
   *  which is why this is a restart and not a relaunch. */
  restart: (workspaceId: string) =>
    api.post<Workspace>(`${V1}/workspaces/${workspaceId}/restart`, {}),
};

export interface CreateJobInput {
  projectId: string;
  name?: string;
  /** Required by the API: a job with no image is rejected with 400. */
  image: string;
  command: string[];
  args?: string[];
  hardwareTier?: string;
}

export const jobsApi = {
  list: (projectId?: string) =>
    api.list<Job>(`${V1}/jobs`, projectId ? { params: { projectId } } : undefined),
  create: (input: CreateJobInput) => api.post<Job>(`${V1}/jobs`, input),
  remove: (jobId: string) => api.delete<void>(`${V1}/jobs/${jobId}`),
  /** Every log endpoint answers `{..., logs}`; this used to be typed as a bare
   *  string, so the viewer received the envelope object and threw on
   *  `content.split`. The panel then rendered nothing at all - the logs looked
   *  missing when they had been fetched successfully. */
  logs: (jobId: string) =>
    api.get<LogsResponse>(`${V1}/jobs/${jobId}/logs`).then((response) => response?.logs ?? ''),
};

export interface CreateCronJobInput extends CreateJobInput {
  schedule: string;
  timeZone: string;
}

export const cronJobsApi = {
  list: (projectId?: string) =>
    api.list<CronJob>(`${V1}/cronjobs`, projectId ? { params: { projectId } } : undefined),
  create: (input: CreateCronJobInput) => api.post<CronJob>(`${V1}/cronjobs`, input),
  remove: (cronJobId: string) => api.delete<void>(`${V1}/cronjobs/${cronJobId}`),
};

/** The API validates this set exactly; anything else is rejected with 400. */
export type AppAccessMode = 'private' | 'organization' | 'users' | 'public';

export interface CreateAppInput {
  projectId: string;
  name?: string;
  slug?: string;
  image: string;
  command: string[];
  args?: string[];
  port: number;
  hardwareTier?: string;
  accessMode?: AppAccessMode;
  /** Required when accessMode is 'organization'. */
  allowedOrganizations?: string[];
  /** Required when accessMode is 'users'. */
  allowedUsers?: string[];
}

export const appsApi = {
  list: (projectId?: string) =>
    api.list<App>(`${V1}/apps`, projectId ? { params: { projectId } } : undefined),
  create: (input: CreateAppInput) => api.post<App>(`${V1}/apps`, input),
  remove: (appId: string) => api.delete<void>(`${V1}/apps/${appId}`),
  /** Hands an application to a person, a team or an organization. An app is
   *  a service and outlives whoever launched it, so somebody has to be able
   *  to answer for it after they leave. */
  setOwner: (appId: string, input: { ownerType: string; ownerId: string }) =>
    api.put<App>(`${V1}/apps/${appId}/owner`, input),
  restart: (appId: string) => api.post<App>(`${V1}/apps/${appId}/restart`),
  stop: (appId: string) => api.post<App>(`${V1}/apps/${appId}/stop`),
  publish: (appId: string, input?: { accessMode?: string; allowedUsers?: string[]; allowedOrganizations?: string[] }) =>
    api.post<App>(`${V1}/apps/${appId}/publish`, input ?? {}),
  /** The envelope, kept: an app that has not started yet is `pending`, and a
   *  watcher polls until it is not - the same contract build logs have. */
  logs: (appId: string) => api.get<LogsResponse>(`${V1}/apps/${appId}/logs`),
  revisions: (appId: string) => api.list<AppRevision>(`${V1}/apps/${appId}/revisions`),
  rollback: (appId: string, revisionId: string) =>
    api.post<App>(`${V1}/apps/${appId}/revisions/${revisionId}/rollback`),
  usage: (appId: string) => api.get<AppUsage>(`${V1}/apps/${appId}/usage`),
};

export const dashboardsApi = {
  list: (projectId?: string) =>
    api.list<App>(`${V1}/dashboards`, projectId ? { params: { projectId } } : undefined),
  create: (input: CreateAppInput & { slug: string }) => api.post<App>(`${V1}/dashboards`, input),
  remove: (dashboardId: string) => api.delete<void>(`${V1}/dashboards/${dashboardId}`),
};

/** Un endpoint appelé par une autre application. Même charge qu'une app ;
 *  ce qui change est que personne ne l'ouvre dans un navigateur. */
export const apisApi = {
  list: (projectId?: string) =>
    api.list<App>(`${V1}/apis`, projectId ? { params: { projectId } } : undefined),
  create: (input: CreateAppInput & { slug: string }) => api.post<App>(`${V1}/apis`, input),
  remove: (apiId: string) => api.delete<void>(`${V1}/apis/${apiId}`),
};

export const projectTokensApi = {
  list: (projectId: string) => api.list<ProjectToken>(`${V1}/projects/${projectId}/tokens`),
  create: (projectId: string, input: { name: string; expiresInDays?: number }) =>
    api.post<{ token: ProjectToken; secret: string; note: string }>(
      `${V1}/projects/${projectId}/tokens`,
      input,
    ),
  remove: (projectId: string, tokenId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/tokens/${tokenId}`),
};

export const productionApi = {
  apps: () => api.list<App>(`${V1}/production/apps`),
};

export interface CreateDatasetInput {
  name: string;
  description?: string;
  classification?: string;
  provider?: string;
  endpoint?: string;
  region?: string;
  bucket?: string;
  prefix?: string;
  accessKey?: string;
  secretKey?: string;
  /** L'organisation a qui appartient une donnee reglementee. Obligatoire pour
   *  une classification HDS : le jour ou la personne nommee part, la donnee de
   *  sante d'une organisation aurait un proprietaire qui n'existe plus. */
  ownerId?: string;
}

export const datasetsApi = {
  list: () => api.list<Dataset>(`${V1}/datasets`),
  create: (input: CreateDatasetInput) => api.post<Dataset>(`${V1}/datasets`, input),
  update: (datasetId: string, input: { name?: string; description?: string }) =>
    api.put<Dataset>(`${V1}/datasets/${datasetId}`, input),
  remove: (datasetId: string) => api.delete<void>(`${V1}/datasets/${datasetId}`),
  /** Remplace les identifiants S3. Ils sont eprouves avant d'etre enregistres :
   *  en sauver qui ne marchent pas ne fait que deplacer la panne. */
  setCredentials: (datasetId: string, input: { accessKey: string; secretKey: string }) =>
    api.put<{ updated: boolean }>(`${V1}/datasets/${datasetId}/credentials`, input),
  setOwner: (datasetId: string, input: { ownerType: string; ownerId: string }) =>
    api.put<Dataset>(`${V1}/datasets/${datasetId}/ownership`, input),

  access: (datasetId: string) => api.list<DatasetAccess>(`${V1}/datasets/${datasetId}/access`),
  grant: (datasetId: string, subjectType: string, subjectId: string, role: string) =>
    api.put<DatasetAccess>(`${V1}/datasets/${datasetId}/access/${subjectType}/${encodeURIComponent(subjectId)}`, {
      role,
    }),
  revoke: (datasetId: string, subjectType: string, subjectId: string) =>
    api.delete<void>(`${V1}/datasets/${datasetId}/access/${subjectType}/${encodeURIComponent(subjectId)}`),

  /** A listing, addressed as one. `objects/<path>` is the route that *fetches*
   *  an object, so navigating into a folder used to ask the platform to
   *  download it - the folder came back as an error and the explorer looked
   *  broken. The folder is a query parameter, and the server returns that one
   *  directory rather than the whole bucket. */
  objects: (datasetId: string, prefix?: string) =>
    api.list<StorageObject>(
      `${V1}/datasets/${datasetId}/objects${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`,
    ),
  usage: (datasetId: string) => api.get<DatasetUsage>(`${V1}/datasets/${datasetId}/usage`),
  createFolder: (datasetId: string, path: string) =>
    api.post<void>(`${V1}/datasets/${datasetId}/folders`, { path }),
  /** `recursive` is what makes deleting a folder delete anything.
   *
   *  Without it the server removes a single key, and a folder is usually only
   *  a prefix inferred from the objects under it - so S3 answers success for a
   *  key that never existed, the screen reports the deletion, and the files
   *  are all still there. */
  deleteObject: (datasetId: string, path: string, recursive = false) =>
    api.delete<void>(
      `${V1}/datasets/${datasetId}/objects/${encodeObjectPath(path)}${recursive ? '?recursive=true' : ''}`,
    ),
  /** Les deux chemins vont dans le corps, pas dans l'URL : un renommage en
   *  porte deux, et la moitie d'un chemin dans l'adresse et l'autre ailleurs
   *  est une occasion d'en nettoyer une seule. */
  renameObject: (datasetId: string, from: string, to: string) =>
    api.post<{ from: string; to: string; size: number }>(
      `${V1}/datasets/${datasetId}/rename`, { from, to }),
  downloadUrl: (datasetId: string, keys: string[]) =>
    api.post<{ url: string }>(`${V1}/datasets/${datasetId}/download-url`, { keys }),
  downloadArchive: (datasetId: string, keys: string[], filename: string) =>
    downloadFile(`${V1}/datasets/${datasetId}/download`, filename, {
      method: 'POST',
      body: { keys },
    }),

  /** Uploads with progress, which fetch cannot report. */
  upload: (
    datasetId: string,
    path: string,
    file: File,
    handlers: {
      onProgress?: (percent: number) => void;
      headers: Record<string, string>;
      signal?: AbortSignal;
    },
  ): Promise<void> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', `${V1}/datasets/${datasetId}/objects/${encodeObjectPath(path)}`);
      for (const [key, value] of Object.entries(handlers.headers)) xhr.setRequestHeader(key, value);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) handlers.onProgress?.(Math.round((event.loaded / event.total) * 100));
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(xhr.responseText || `Échec du téléversement (${xhr.status})`));
      xhr.onerror = () => reject(new Error('Échec du téléversement : la plateforme est injoignable.'));
      xhr.onabort = () => reject(new Error('Téléversement annulé.'));
      handlers.signal?.addEventListener('abort', () => xhr.abort());
      xhr.send(file);
    }),

  readObject: (datasetId: string, path: string) =>
    request<Response>(`${V1}/datasets/${datasetId}/objects/${encodeObjectPath(path)}`, { raw: true }),
};

export const datasourcesApi = {
  list: () => api.list<Datasource>(`${V1}/datasources`),
  definitions: () => api.list<DatasourceDefinition>(`${V1}/datasource-definitions`),
  create: (input: Record<string, unknown>) => api.post<Datasource>(`${V1}/datasources`, input),
  get: (datasourceId: string) => api.get<Datasource>(`${V1}/datasources/${datasourceId}`),
  /** Changes what a connector points at. Write on it, or its ownership: a
   *  host changed here redirects every workspace that mounts it. */
  update: (datasourceId: string, input: Record<string, unknown>) =>
    api.put<Datasource>(`${V1}/datasources/${datasourceId}`, input),
  remove: (datasourceId: string) => api.delete<void>(`${V1}/datasources/${datasourceId}`),
  /** Hands a connector to a person, a team or an organization. It points at a
   *  system the organization runs, so it outlives whoever declared it. */
  setOwner: (datasourceId: string, input: { ownerType: string; ownerId: string }) =>
    api.put<Datasource>(`${V1}/datasources/${datasourceId}/ownership`, input),
  /** Qui peut utiliser ce connecteur, et les deux gestes pour le dire.
   *
   *  Pretter plutot que ceder : une equipe qui travaille sur la meme base en
   *  declarait un chacun, meme hote et memes identifiants recopies a la main. */
  access: (datasourceId: string) =>
    api.list<DatasourceAccess>(`${V1}/datasources/${datasourceId}/access`),
  grant: (datasourceId: string, subjectType: string, subjectId: string, role: string) =>
    api.put<DatasourceAccess>(
      `${V1}/datasources/${datasourceId}/access/${subjectType}/${encodeURIComponent(subjectId)}`,
      { role },
    ),
  revoke: (datasourceId: string, subjectType: string, subjectId: string) =>
    api.delete<void>(
      `${V1}/datasources/${datasourceId}/access/${subjectType}/${encodeURIComponent(subjectId)}`,
    ),
  validate: (datasourceId: string) =>
    api.post<{ reachable: boolean; error?: string }>(`${V1}/datasources/${datasourceId}/validate`),
  restart: (datasourceId: string) => api.post<Datasource>(`${V1}/datasources/${datasourceId}/restart`),
  logs: (datasourceId: string) =>
    api.get<LogsResponse>(`${V1}/datasources/${datasourceId}/logs`).then((response) => response?.logs ?? ''),
  createService: (input: Record<string, unknown>) => api.post<Datasource>(`${V1}/dataservices`, input),
};

export const teamsApi = {
  /** The teams you belong to.
   *
   *  Exactly what the owner selector needs: the server refuses a transfer to a
   *  group you are not part of, so offering one would be offering a refusal. */
  mine: () => api.list<Team>(`${V1}/user/teams`),
};

/** Ou un objet du catalogue est monte.
 *
 *  Le rattachement n'avait qu'un sens a l'ecran : le projet listait ce qu'il
 *  montait, l'objet ne disait rien de la ou il etait monte - ce qui est a
 *  l'envers pour un extrait, qui existe pour etre repris par plusieurs
 *  projets.
 *
 *  `hidden` compte les projets que l'appelant n'a pas a connaitre. "Monte dans
 *  2 projets que vous ne voyez pas" est une reponse vraie ; le silence se
 *  lirait "monte nulle part" et inviterait a supprimer. */
/** La regle de lecture d'un dataset : la lire, l'essayer, l'enregistrer.
 *
 *  L'assistant savait deja diagnostiquer une disposition a partir des formes de
 *  chemins, et il n'existait aucun endroit ou ecrire sa reponse : la
 *  conversation s'arretait dans le panneau (ADR-040). Ce qui manquait n'etait
 *  pas l'intelligence, c'etait un endroit pour noter le resultat. */
export const pathLayoutApi = {
  get: (datasetId: string) =>
    api.get<DatasetPathLayout>(`${V1}/datasets/${datasetId}/path-layout`),
  set: (datasetId: string, input: Record<string, number | string | null>) =>
    api.put<DatasetPathLayout>(`${V1}/datasets/${datasetId}/path-layout`, input),
  /** Ce que la regle lirait, sur de vrais chemins, sans rien enregistrer. */
  try: (datasetId: string, input: Record<string, number | string | null>) =>
    api.post<DatasetPathLayoutTrial>(`${V1}/datasets/${datasetId}/path-layout/trials`, input),
};

/** Ce qu'une ontologie dit du sens des donnees (ADR-047).
 *
 *  Sur l'ontologie et non sur le dataset : un dataset est un bucket, le sens
 *  appartient a la couche au-dessus. Un rescan remplace le manifeste et garde
 *  l'ontologie, donc une card posee a cote du manifeste survit au rescan - ce
 *  que la premiere version avait lu a l'envers. */
export const ontologyCardApi = {
  get: (ontologyId: string) => api.get<OntologyCard>(`${V1}/ontologies/${ontologyId}/card`),
  set: (ontologyId: string, input: Record<string, unknown>) =>
    api.put<OntologyCard>(`${V1}/ontologies/${ontologyId}/card`, input),
};

/** Ce que supprimer emporte.
 *
 *  Une ontologie detruit ses extraits - un extrait est une selection sur ses
 *  sujets et ne veut rien dire sans elle. Un dataset ne detruit rien et laisse
 *  ses ontologies decrire une source disparue, ce qui est pire a sa facon :
 *  l'ecran continue de les montrer et elles ont l'air utilisables.
 *
 *  Demande au clic plutot que calcule pour chaque ligne : une liste de
 *  quarante paierait quarante reponses que personne ne lit. */
export const deletionCostApi = {
  ontology: (ontologyId: string) =>
    api.get<DeletionCost>(`${V1}/ontologies/${ontologyId}/deletion-cost`),
  dataset: (datasetId: string) =>
    api.get<DeletionCost>(`${V1}/datasets/${datasetId}/deletion-cost`),
};

export const mountsApi = {
  dataset: (datasetId: string) =>
    api.get<MountedProjectList>(`${V1}/datasets/${datasetId}/projects`),
  ontology: (ontologyId: string) =>
    api.get<MountedProjectList>(`${V1}/ontologies/${ontologyId}/projects`),
  extract: (extractId: string) =>
    api.get<MountedProjectList>(`${V1}/extracts/${extractId}/projects`),
};

export const ontologiesApi = {
  /** The scan needs to be told which dataset to read: without a datasetId the
   *  API answers 400, and the previous signature sent no body at all - which is
   *  one reason no screen could launch a scan.
   *
   *  No project. The server route says why in its own comment: an ontology
   *  describes a dataset and carries no project. Sending one through
   *  /projects/{id}/ontology/scans made the project a precondition of
   *  scanning, and with no project chosen the path became /projects//... -
   *  which the router answered, correctly, with "no such endpoint".
   *
   *  Attaching the result to a project is a separate call that already exists:
   *  PUT /projects/{projectID}/ontologies/{ontologyID}. */
  scanOntology: (input: { datasetId: string; inferenceProfile?: string }) =>
    api.post<{
      manifest?: OntologyManifestSummary;
      item?: Ontology;
      refreshed?: boolean;
      /** Ce qu'un rafraichissement a change, et pourquoi. Absent au premier
       *  scan, et absent quand le manifeste precedent est anterieur a
       *  l'enregistrement de la regle : dire "la lecture a change" a tort
       *  enverrait chercher une regle que personne n'a editee. */
      changed?: {
        readingChanged: boolean;
        previousReading?: string;
        currentReading?: string;
        previousObjects: number;
        currentObjects: number;
        previousSubjects: number;
        currentSubjects: number;
      };
    }>(
      `${V1}/ontologies/scans`,
      input,
    ),
  list: () => api.list<Ontology>(`${V1}/ontologies`),
  update: (ontologyId: string, input: Record<string, unknown>) =>
    api.put<Ontology>(`${V1}/ontologies/${ontologyId}`, input),
  remove: (ontologyId: string) => api.delete<void>(`${V1}/ontologies/${ontologyId}`),
  setOwner: (ontologyId: string, input: { ownerType: string; ownerId: string }) =>
    api.put<Ontology>(`${V1}/ontologies/${ontologyId}/ownership`, input),
  /** Counts what the source holds now and compares it with the manifest. The
   *  count is a listing: the platform reads how many objects are there and
   *  writes nothing back. */
  freshness: (ontologyId: string) =>
    api.get<OntologyFreshness>(`${V1}/ontologies/${ontologyId}/freshness`),
  completeness: (ontologyId: string) =>
    api.get<OntologyCompleteness>(`${V1}/ontologies/${ontologyId}/completeness`),
  /** Les photographies que cette ontologie a traversees, la plus recente en
   *  tete, avec ce que chacune a change par rapport a la precedente. */
  scans: (ontologyId: string) =>
    api.get<{ scans: OntologyScan[] }>(`${V1}/ontologies/${ontologyId}/scans`),
  /** Every extract the caller can reach, for the catalogue and for the picker
   *  that attaches one to a project. */
  allExtracts: () => api.list<Extract>(`${V1}/extracts`),
  extracts: (ontologyId: string) => api.list<Extract>(`${V1}/ontologies/${ontologyId}/extracts`),
  createExtract: (ontologyId: string, input: Record<string, unknown>) =>
    api.post<Extract>(`${V1}/ontologies/${ontologyId}/extracts`, input),
  /** La liste gelee elle-meme : tout l'objet, et aucun ecran ne la montrait. */
  extractMembers: (extractId: string, limit = 200) =>
    api.get<ExtractMembers>(`${V1}/extracts/${extractId}/members?limit=${limit}`),
  /** Et si ces fichiers sont encore la : geler protege le n, pas les octets. */
  extractIntegrity: (extractId: string) =>
    api.get<ExtractIntegrity>(`${V1}/extracts/${extractId}/integrity`),
  deleteExtract: (extractId: string) => api.delete<void>(`${V1}/extracts/${extractId}`),
  /** Qui peut lire cette ontologie, et les deux gestes pour le dire. */
  ontologyAccess: (ontologyId: string) =>
    api.list<OntologyAccess>(`${V1}/ontologies/${ontologyId}/access`),
  grantOntology: (ontologyId: string, subjectType: string, subjectId: string, role: string) =>
    api.put<OntologyAccess>(
      `${V1}/ontologies/${ontologyId}/access/${subjectType}/${encodeURIComponent(subjectId)}`,
      { role },
    ),
  revokeOntology: (ontologyId: string, subjectType: string, subjectId: string) =>
    api.delete<void>(
      `${V1}/ontologies/${ontologyId}/access/${subjectType}/${encodeURIComponent(subjectId)}`,
    ),

  /** Qui peut lire cet extrait, et les deux gestes pour le dire. */
  extractAccess: (extractId: string) => api.list<ExtractAccess>(`${V1}/extracts/${extractId}/access`),
  grantExtract: (extractId: string, subjectType: string, subjectId: string) =>
    api.put<ExtractAccess>(
      `${V1}/extracts/${extractId}/access/${subjectType}/${encodeURIComponent(subjectId)}`,
      {},
    ),
  revokeExtract: (extractId: string, subjectType: string, subjectId: string) =>
    api.delete<void>(
      `${V1}/extracts/${extractId}/access/${subjectType}/${encodeURIComponent(subjectId)}`,
    ),
  /** Hands an extract to a person, a team or an organization. */
  setExtractOwner: (extractId: string, input: { ownerType: string; ownerId: string }) =>
    api.put<Extract>(`${V1}/extracts/${extractId}/ownership`, input),
  /** Corrige le libelle, et lui seul : la liste de fichiers figee, l'auteur et
   *  les dates sont ce qui fait l'extrait. Redeclarer la selection pour
   *  corriger un nom en figeait une autre, sur un bucket qui grandit. */
  updateExtract: (extractId: string, input: { name: string; description: string }) =>
    api.put<Extract>(`${V1}/extracts/${extractId}`, input),
  /** Which projects mount it. Attaching is a link, so several may. */
  attachExtract: (projectId: string, extractId: string) =>
    api.put<void>(`${V1}/projects/${projectId}/extracts/${extractId}`, {}),
  detachExtract: (projectId: string, extractId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/extracts/${extractId}`),
  access: (ontologyId: string) => api.list<OntologyAccess>(`${V1}/ontologies/${ontologyId}/access`),
  grant: (ontologyId: string, subjectType: string, subjectId: string, role: string) =>
    api.put<OntologyAccess>(
      `${V1}/ontologies/${ontologyId}/access/${subjectType}/${encodeURIComponent(subjectId)}`,
      { role },
    ),
  revoke: (ontologyId: string, subjectType: string, subjectId: string) =>
    api.delete<void>(`${V1}/ontologies/${ontologyId}/access/${subjectType}/${encodeURIComponent(subjectId)}`),
  /** The API filters on `object`, which it also matches against the object's
   *  type and parent. It was sent `{ query }` - a field the request struct does
   *  not have - so every question decoded to an empty filter and came back with
   *  the whole manifest. The screen then displayed that as the answer, which is
   *  worse than an error: it looks like a reply. */
  query: (ontologyId: string, filter: string) =>
    api.post<{ items?: OntologyQueryItem[]; count?: number; limited?: boolean; error?: string }>(
      `${V1}/ontologies/${ontologyId}/query`,
      { object: filter, limit: 100 },
    ),
};

export const repositoriesApi = {
  list: () => api.list<Repository>(`${V1}/repositories`),
  create: (input: Record<string, unknown>) => api.post<Repository>(`${V1}/repositories`, input),
  update: (repositoryId: string, input: Record<string, unknown>) =>
    api.put<Repository>(`${V1}/repositories/${repositoryId}`, input),
  remove: (repositoryId: string) => api.delete<void>(`${V1}/repositories/${repositoryId}`),
  validate: (repositoryId: string) =>
    api.post<{ reachable: boolean; error?: string }>(`${V1}/repositories/${repositoryId}/validate`),
};

export const secretsApi = {
  list: () => api.list<Secret>(`${V1}/secrets`),
  get: (name: string) => api.get<Secret>(`${V1}/secrets/${encodeURIComponent(name)}`),
  create: (input: {
    name: string;
    value: string;
    type?: string;
    expiresAt?: string | null;
    /** Says the lifetime question was answered, rather than left blank. The
     *  API requires one or the other for a token. */
    neverExpires?: boolean;
  }) => api.post<Secret>(`${V1}/secrets`, input),
  remove: (name: string) => api.delete<void>(`${V1}/secrets/${encodeURIComponent(name)}`),
};

export const environmentsApi = {
  list: (projectId?: string) =>
    api.list<Environment>(`${V1}/environments`, projectId ? { params: { projectId } } : undefined),
  /** Encoded: an environment's identifier carries the image reference, so it
   *  holds slashes. Sent raw it became extra path segments and the router
   *  answered "Method Not Allowed" to somebody deleting an environment. */
  remove: (environmentId: string) =>
    api.delete<void>(`${V1}/environments/${encodeURIComponent(environmentId)}`),
  builds: (projectId?: string) =>
    api.list<Build>(`${V1}/builds`, projectId ? { params: { projectId } } : undefined),
  /** Typed on purpose: this took `Record<string, unknown>`, so a form that
   *  sent none of the fields the API requires compiled cleanly and failed at
   *  the click. Either a repository to clone or a Dockerfile to build. */
  createBuild: (input: {
    projectId: string;
    name?: string;
    dockerfileContent?: string;
    gitRepository?: string;
    gitRef?: string;
    dockerfilePath?: string;
    contextPath?: string;
    destinationImage?: string;
  }) => api.post<Build>(`${V1}/builds`, input),
  buildLogs: (buildId: string) =>
    api.get<{ status: string; logs: string; pending?: boolean; unavailable?: string }>(
      `${V1}/builds/${buildId}/logs`,
    ),
  cancelBuild: (buildId: string) => api.delete<void>(`${V1}/builds/${buildId}`),
  /** The API answers with the file *and* where it came from - repository, ref,
   *  path, source URL. The screen shows the file, so unwrap it here rather than
   *  letting a caller render the envelope: typing this as a string was how the
   *  editor came to display "[object Object]". */
  dockerfile: async (buildId: string) => {
    const answer = await api.get<DockerfileResponse | string>(
      `${V1}/builds/${buildId}/dockerfile`,
    );
    return typeof answer === 'string' ? answer : (answer?.content ?? '');
  },
};

/** Les octrois d'un projet a des equipes.
 *
 * Jumeau de celui des organisations : c'est l'administrateur du projet qui
 * decide quelles equipes l'atteignent, jamais qui les compose.
 */
export const projectTeamRolesApi = {
  list: (projectId: string) =>
    api.list<ProjectTeamRole>(`${V1}/projects/${projectId}/team-roles`),
  grant: (projectId: string, teamId: string, role: string) =>
    api.put<ProjectTeamRole>(
      `${V1}/projects/${projectId}/team-roles/${encodeURIComponent(teamId)}`,
      { role },
    ),
  revoke: (projectId: string, teamId: string) =>
    api.delete<void>(`${V1}/projects/${projectId}/team-roles/${encodeURIComponent(teamId)}`),
};

export const projectOrganizationRolesApi = {
  list: (projectId: string) =>
    api.list<ProjectOrganizationRole>(`${V1}/projects/${projectId}/organization-roles`),
  grant: (projectId: string, organizationId: string, role: string) =>
    api.put<ProjectOrganizationRole>(
      `${V1}/projects/${projectId}/organization-roles/${encodeURIComponent(organizationId)}`,
      { role },
    ),
  revoke: (projectId: string, organizationId: string) =>
    api.delete<void>(
      `${V1}/projects/${projectId}/organization-roles/${encodeURIComponent(organizationId)}`,
    ),
};

export const egressApi = {
  profiles: () => api.list<EgressProfile>(`${V1}/egress/profiles`),
  request: (input: Record<string, unknown>) => api.post<EgressRule>(`${V1}/egress/rules`, input),
  adminList: () => api.list<EgressRule>(`${V1}/admin/egress/rules`),
  decide: (ruleId: string, input: { status: string; decisionNote?: string }) =>
    api.put<EgressRule>(`${V1}/admin/egress/rules/${ruleId}`, input),
};

export const adminApi = {
  /** Service accounts: principals that are not people (ADR-039). Distinct
   *  from the component credentials below, which are the platform's own. */
  serviceAccounts: () => api.list<ServiceAccount>(`${V1}/admin/service-accounts`),
  createServiceAccount: (input: {
    username: string;
    purpose?: string;
    organizationId?: string;
    responsibleUserId: string;
  }) => api.post<ServiceAccount>(`${V1}/admin/service-accounts`, input),
  createServiceAccountToken: (
    username: string,
    input: { name?: string; scopes: string[]; expiresInDays?: number },
  ) =>
    api.post<{ token: ApiToken; secret: string; note: string }>(
      `${V1}/admin/service-accounts/${encodeURIComponent(username)}/tokens`,
      input,
    ),
  disableServiceAccount: (username: string) =>
    api.delete<{ username: string; tokensRevoked: number }>(
      `${V1}/admin/service-accounts/${encodeURIComponent(username)}`,
    ),
  /** Service accounts: the credentials the platform's own components and a
   *  customer's automation authenticate with. Administered, never personal. */
  componentTokens: () => api.get<{ items: ApiToken[]; scopes: string[] }>(`${V1}/admin/component-tokens`),
  createComponentToken: (input: { component: string; name?: string; scopes: string[]; expiresAt?: string }) =>
    api.post<{ token: ApiToken; secret: string; note: string }>(`${V1}/admin/component-tokens`, input),
  revokeComponentToken: (tokenId: string) =>
    api.delete<void>(`${V1}/admin/component-tokens/${encodeURIComponent(tokenId)}`),
  rotateComponentToken: (tokenId: string) =>
    api.post<{ token: ApiToken; secret: string; note: string }>(
      `${V1}/admin/component-tokens/${encodeURIComponent(tokenId)}/rotate`,
      {},
    ),
  overview: () => api.get<AdminOverview>(`${V1}/admin/overview`),
  softwareInventory: () => api.get<SoftwareInventory>(`${V1}/admin/software-inventory`),
  smtp: () => api.get<SmtpState>(`${V1}/admin/smtp`),
  updateSmtp: (input: Partial<SmtpSettings> & { password?: string }) =>
    api.put<SmtpState>(`${V1}/admin/smtp`, input),
  testSmtp: (input: Partial<SmtpSettings> & { password?: string; testRecipient: string }) =>
    api.post<{ sent: boolean; recipient: string }>(`${V1}/admin/smtp/tests`, input),
  ownedBy: (userId: string) =>
    api.get<{ owns: OwnedResources; count: number }>(
      `${V1}/admin/users/${encodeURIComponent(userId)}/owned`,
    ),
  deactivateUser: (userId: string, successorUserId: string) =>
    api.post<{ disabled: string; transferred: OwnedResources; tokensRevoked: number }>(
      `${V1}/admin/users/${encodeURIComponent(userId)}/deactivation`,
      { successorUserId },
    ),
  reactivateUser: (userId: string) =>
    api.delete<void>(`${V1}/admin/users/${encodeURIComponent(userId)}/deactivation`),
  /** Removes the account for good. The API refuses unless it is already
   *  disabled, and unless what it owns has somewhere to go. */
  deleteUser: (userId: string, successorUserId?: string) =>
    api.delete<{
      deleted: string;
      username: string;
      successor: string;
      tokensRevoked: number;
      secretsDeleted: number;
    }>(`${V1}/admin/users/${encodeURIComponent(userId)}`, undefined, {
      successorUserId: successorUserId ?? '',
    }),
  sendPasswordResetEmail: (userId: string) =>
    api.post<{ sent: boolean }>(`${V1}/admin/users/${encodeURIComponent(userId)}/password-reset-email`, {}),
  usage: () => api.get<{ from: string; to: string; items: UsageTotal[] }>(`${V1}/admin/usage`),
  /** L'activite, distincte de `usage` qui compte des vCPU-heures. */
  activity: (window: string) =>
    api.get<ActivityReport>(`${V1}/admin/activity?window=${encodeURIComponent(window)}`),
  setProjectQuota: (projectId: string, input: Partial<ProjectQuota>) =>
    api.put<ProjectQuotaState>(`${V1}/admin/projects/${encodeURIComponent(projectId)}/quota`, input),
  hardwareTiers: () => api.list<AdminHardwareTier>(`${V1}/admin/hardware-tiers`),
  saveHardwareTier: (tier: AdminHardwareTier) =>
    api.put<AdminHardwareTier>(`${V1}/admin/hardware-tiers/${encodeURIComponent(tier.id)}`, tier),
  removeHardwareTier: (tierId: string) =>
    api.delete<void>(`${V1}/admin/hardware-tiers/${encodeURIComponent(tierId)}`),
  healthHistory: (days?: number) =>
    api.get<HealthHistory>(`${V1}/admin/health/history`, days ? { params: { days: String(days) } } : undefined),
  health: () => api.get<HealthReport>(`${V1}/admin/health`),
  settings: () => api.list<EffectiveSetting>(`${V1}/admin/settings`),
  updateSetting: (key: string, value: string) =>
    api.put<{ items: EffectiveSetting[] }>(`${V1}/admin/settings/${encodeURIComponent(key)}`, { value }),
  users: () => api.list<PlatformUser>(`${V1}/admin/users`),
  createUser: (input: {
    roleKey?: string;
    username: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    organizationId?: string;
  }) => api.post<CreatedUser>(`${V1}/admin/users`, input),
  // The username is deliberately absent: Noryx identifies a person by it, so
  // a rename would orphan their roles, their teams and their audit trail.
  updateUser: (userId: string, input: { firstName?: string; lastName?: string; email?: string }) =>
    api.patch<PlatformUser>(`${V1}/admin/users/${encodeURIComponent(userId)}`, input),
  resetUserPassword: (userId: string) =>
    api.post<CreatedUser>(`${V1}/admin/users/${encodeURIComponent(userId)}/password`, undefined),
  inventory: () => api.get<AdminInventory>(`${V1}/admin/inventory`),
  modules: () => api.list<ModuleInfo>(`${V1}/admin/modules`),
  executions: () => api.list<Execution>(`${V1}/admin/executions`),
  killExecution: (kind: string, executionId: string) =>
    api.delete<void>(`${V1}/admin/executions/${kind}/${executionId}`),
  pods: () => api.list<PodInfo>(`${V1}/pods`),

  organizations: () => api.list<Organization>(`${V1}/admin/organizations`),
  agentGovernance: () => api.get<AgentGovernanceReport>(`${V1}/admin/agent-governance`),
  exportAgentGovernanceCSV: () =>
    downloadFile(`${V1}/admin/agent-governance.csv`, 'noryx-agents.csv'),
  createOrganization: (input: { name: string; alias?: string }) =>
    api.post<Organization>(`${V1}/admin/organizations`, input),
  removeOrganization: (organizationId: string) =>
    api.delete<void>(`${V1}/admin/organizations/${organizationId}`),
  organizationMembers: (organizationId: string) =>
    api.list<OrganizationMember>(`${V1}/admin/organizations/${organizationId}/members`),
  /** Qui administre ce que l'organisation possede.
   *
   *  Un droit nomme, pas une appartenance : l'annuaire dit ou quelqu'un
   *  travaille, il ne dit pas ce qu'il peut faire. Tant que personne n'est
   *  nomme, seuls les administrateurs de la plateforme agissent pour elle. */
  /** La matrice de creation : fonctions en ligne, roles en colonne. */
  platformRoles: () =>
    api.get<{ capabilities: string[]; roles: PlatformRole[] }>(`${V1}/admin/platform-roles`),
  upsertPlatformRole: (key: string, input: { name: string; description: string; capabilities: string[] }) =>
    api.put<PlatformRole>(`${V1}/admin/platform-roles/${encodeURIComponent(key)}`, input),
  deletePlatformRole: (key: string) =>
    api.delete<void>(`${V1}/admin/platform-roles/${encodeURIComponent(key)}`),
  userPlatformRole: (userId: string) =>
    api.get<{ userId: string; role: PlatformRole; assigned: boolean }>(
      `${V1}/admin/users/${encodeURIComponent(userId)}/platform-role`,
    ),
  setUserPlatformRole: (userId: string, roleKey: string) =>
    api.put<PlatformRoleAssignment>(`${V1}/admin/users/${encodeURIComponent(userId)}/platform-role`, { roleKey }),
  clearUserPlatformRole: (userId: string) =>
    api.delete<void>(`${V1}/admin/users/${encodeURIComponent(userId)}/platform-role`),
  organizationPolicy: (organizationId: string) =>
    api.get<OrganizationPolicy>(`${V1}/admin/organizations/${organizationId}/policy`),
  setOrganizationPolicy: (organizationId: string, membershipGrant: string) =>
    api.put<OrganizationPolicy>(`${V1}/admin/organizations/${organizationId}/policy`, { membershipGrant }),
  organizationAdmins: (organizationId: string) =>
    api.list<OrganizationAdmin>(`${V1}/admin/organizations/${organizationId}/administrators`),
  addOrganizationAdmin: (organizationId: string, subjectType: string, subjectId: string) =>
    api.put<OrganizationAdmin>(
      `${V1}/admin/organizations/${organizationId}/administrators/${subjectType}/${encodeURIComponent(subjectId)}`,
      {},
    ),
  removeOrganizationAdmin: (organizationId: string, subjectType: string, subjectId: string) =>
    api.delete<void>(
      `${V1}/admin/organizations/${organizationId}/administrators/${subjectType}/${encodeURIComponent(subjectId)}`,
    ),
  addOrganizationMember: (organizationId: string, userId: string) =>
    api.put<void>(`${V1}/admin/organizations/${organizationId}/members/${encodeURIComponent(userId)}`),
  removeOrganizationMember: (organizationId: string, userId: string) =>
    api.delete<void>(`${V1}/admin/organizations/${organizationId}/members/${encodeURIComponent(userId)}`),

  teams: (organizationId: string) =>
    api.list<Team>(`${V1}/admin/organizations/${organizationId}/teams`),
  createTeam: (organizationId: string, input: { name: string; description?: string }) =>
    api.post<Team>(`${V1}/admin/organizations/${organizationId}/teams`, input),
  updateTeam: (teamId: string, input: { name: string; description?: string }) =>
    api.put<Team>(`${V1}/admin/teams/${teamId}`, input),
  removeTeam: (teamId: string) => api.delete<void>(`${V1}/admin/teams/${teamId}`),
  teamMembers: (teamId: string) => api.list<TeamMember>(`${V1}/admin/teams/${teamId}/members`),
  addTeamMember: (teamId: string, userId: string) =>
    api.post<void>(`${V1}/admin/teams/${teamId}/members`, { userId }),
  removeTeamMember: (teamId: string, userId: string) =>
    api.delete<void>(`${V1}/admin/teams/${teamId}/members/${encodeURIComponent(userId)}`),

  audit: (params?: Record<string, string>) => api.list<AuditEvent>(`${V1}/admin/audit`, { params }),
  downloadAudit: () => downloadFile(`${V1}/admin/audit.csv`, 'noryx-audit.csv'),

  teamUsage: (from: string, to: string) =>
    api.get<TeamUsageReport>(`${V1}/admin/team-usage`, { params: { from, to } }),
  downloadTeamUsage: (from: string, to: string) =>
    downloadFile(`${V1}/admin/team-usage.csv?from=${from}&to=${to}`, 'noryx-team-usage.csv'),
  dataUsage: () => api.get<DataUsageReport>(`${V1}/admin/data-usage`),
  downloadDataUsage: () => downloadFile(`${V1}/admin/data-usage.csv`, 'noryx-data-usage.csv'),

  rbacMatrix: () => api.get<RbacMatrixReport>(`${V1}/admin/rbac-matrix`),
  downloadRbacMatrix: () => downloadFile(`${V1}/admin/rbac-matrix.csv`, 'noryx-rbac-matrix.csv'),
  rbacPolicy: () => api.get<RbacPolicyResponse>(`${V1}/admin/rbac-policy`),
  saveRbacPolicy: (rows: RbacPolicyRow[]) =>
    api.put<RbacPolicyResponse>(`${V1}/admin/rbac-policy`, { rows }),

  storageCapacity: () => api.get<StorageCapacityReport>(`${V1}/admin/storage-capacity`),
  storageEndpoints: () => api.list<StorageEndpoint>(`${V1}/admin/storage-endpoints`),
  createStorageEndpoint: (input: Record<string, unknown>) =>
    api.post<StorageEndpoint>(`${V1}/admin/storage-endpoints`, input),
  updateStorageEndpoint: (endpointId: string, input: Record<string, unknown>) =>
    api.put<StorageEndpoint>(`${V1}/admin/storage-endpoints/${endpointId}`, input),
  removeStorageEndpoint: (endpointId: string) =>
    api.delete<void>(`${V1}/admin/storage-endpoints/${endpointId}`),
  testStorageEndpoint: (endpointId: string) =>
    api.post<{ reachable: boolean; error?: string }>(`${V1}/admin/storage-endpoints/${endpointId}/test`),

  backupStatus: () => api.get<BackupConfigStatus>(`${V1}/admin/backups/config/status`),
  saveBackupConfig: (input: Record<string, unknown>) =>
    api.put<BackupConfigStatus>(`${V1}/admin/backups/config`, input),
  backupRuns: () => api.list<BackupRun>(`${V1}/admin/backups/runs`),
  runBackup: () => api.post<BackupRun>(`${V1}/admin/backups/runs`),
  backupReport: (runId: string) => api.get<string>(`${V1}/admin/backups/runs/${runId}/report`),
};

